/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { FC, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Form, FormInstance, Spin } from 'antd';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMutation, useQuery, useReactiveVar } from '@apollo/client';
import {
  stepList,
  currentSubStep,
  loadingCredit,
  customerRut,
  customerInputInfo,
} from 'apollo/cache';
import {
  Customer,
  CustomerInput,
  CustomerForm,
  PaymentMethod,
  AllowedPaymentMethodType,
  FinancingStatus,
  CreditStates,
} from 'apollo/types';
import operations from 'apollo/operations';
import { FinancialInformationForm, PersonalInformationForm } from 'components/customer';
import getStepIndexByPathname from 'utils/getStepIndexByPathname';
import formatDateToValidJsStringFormat from 'utils/formatDateToValidJsStringFormat';
import { NextButton } from 'components/ui';
import formatRUT from 'utils/rutFormatter';

dayjs.extend(utc);

interface FormProps {
  form: FormInstance;
}

const PersonalInformation: FC<FormProps> = (props: FormProps): JSX.Element => {
  const history = useHistory();

  const { form } = props;
  const [loadingInjection, setLoadingInjection] = useState(false);
  const [filledPersonalInformation, setFilledPersonalInformation] = useState(false);
  const steps = useReactiveVar(stepList);
  const currentStepIndex = getStepIndexByPathname(history.location.pathname, steps);
  const currentSubStepIndex = useReactiveVar(currentSubStep);
  const customerRutCache = useReactiveVar(customerRut);

  const { data: customerInfo, loading: customerLoading } = useQuery<{
    customer: Customer;
  }>(operations.GET_CUSTOMER_INFO, {
    fetchPolicy: 'network-only',
  });

  const { data: paymentMethodData } = useQuery<{
    paymentMethod: PaymentMethod;
  }>(operations.GET_PAYMENT_METHOD, {
    fetchPolicy: 'network-only',
  });

  const [
    // eslint-disable-next-line
    requestFinancingPreApproval,
    { data: financingStatus, loading: financingStatusLoading },
  ] = useMutation<{
    requestPreApprovedCredit: PaymentMethod;
  }>(operations.REQUEST_FINANCING_PRE_APPROVAL, {
    fetchPolicy: 'network-only',
  });

  const next = () => {
    const isPaymentMethodCash =
      paymentMethodData &&
      paymentMethodData.paymentMethod &&
      paymentMethodData.paymentMethod.type === AllowedPaymentMethodType.Cash;
    const isPaymentMethodFinancing =
      paymentMethodData &&
      paymentMethodData.paymentMethod &&
      paymentMethodData.paymentMethod.type === AllowedPaymentMethodType.WithFinancing;
    if (isPaymentMethodCash) {
      history.push({
        pathname: steps[currentStepIndex + 1].route,
        state: {
          from: history.location,
        },
      });
    } else if (
      isPaymentMethodFinancing &&
      customerInfo &&
      customerInfo.customer &&
      customerInfo.customer.financingInfo
    ) {
      currentSubStep(-1);
      history.push({
        pathname: steps[currentStepIndex + 1].route,
        state: {
          from: history.location,
        },
      });
    }
  };

  const submitCustomerInfo = (values: CustomerForm) => {
    const birthdayISO = formatDateToValidJsStringFormat(values.birthday);
    const rut = formatRUT(customerRutCache as string);
    let customerInput: CustomerInput = {
      name: values.name?.trim() || '',
      firstSurname: values.firstSurname?.trim() || '',
      secondSurname: values.secondSurname?.trim() || '',
      rut: rut || '11111111-1',
      email: values?.email || 'test@test.com',
      phone: values?.phone || '',
      address: values?.address?.trim() || '',
      addressNumber: values?.addressNumber || '',
      commune: values.commune.split('#')[1] || '',
      region: values.region?.split('#')[1] || '',
      regionId: (form.getFieldValue('regionId') as string | undefined)?.toString() || '',
      communeId:
        (form.getFieldValue('communeId') as string | undefined)?.toString() || '',
      birthday: new Date(birthdayISO.slice(0, birthdayISO.indexOf('T'))).toISOString(),
    };

    const unformattedValue = values.income?.toString().replace(/\$\s?|(\.*)/g, '');
    const numberIncome = Number(unformattedValue);
    customerInput = {
      ...customerInput,
      financingInfo: {
        income: {
          Type: (values.incomeType as string) || '',
          Value: numberIncome || 0,
          Old: '',
        },
        profile: {
          Gender: '',
          RelationStatus: '',
          Studies: '',
          ContractType: (values.contractType as string) || '',
          IncomeType: (values.incomeVariability as string) || '',
          Profession: values.profession || '',
        },
      },
    };
    customerInputInfo(customerInput);

    const isPaymentMethodFinancing =
      paymentMethodData &&
      paymentMethodData.paymentMethod &&
      paymentMethodData.paymentMethod.type === AllowedPaymentMethodType.WithFinancing;
    if (isPaymentMethodFinancing && filledPersonalInformation) {
      customerInput = {
        ...customerInput,
        financingInfo: {
          income: {
            Type: (values.incomeType as string) || '',
            Value: numberIncome || 0,
            Old: '',
          },
          profile: {
            Gender: '',
            RelationStatus: '',
            Studies: '',
            ContractType: (values.contractType as string) || '',
            IncomeType: (values.incomeVariability as string) || '',
            Profession: values.profession || '',
          },
        },
      };
    } else {
      setFilledPersonalInformation(true);
      currentSubStep(currentSubStepIndex + 1);
    }

    history.push({
      pathname: steps[currentStepIndex + 1].route,
      state: {
        from: history.location,
      },
    });
  };

  useEffect(() => {
    if (
      paymentMethodData &&
      paymentMethodData.paymentMethod &&
      paymentMethodData.paymentMethod.type === AllowedPaymentMethodType.WithFinancing
    ) {
      const newActiveSteps = [...steps];
      newActiveSteps[currentStepIndex] = {
        ...steps[currentStepIndex],
        subSteps: [
          {
            viewTitle: 'Datos personales para la financiera',
            title: 'Datos para el financiamiento',
            subTitle: 'Rellena todos los campos',
          },
        ],
      };
      stepList(newActiveSteps);
    } else if (paymentMethodData && paymentMethodData.paymentMethod) {
      currentSubStep(-1);
      const newActiveSteps = [...steps];
      newActiveSteps[currentStepIndex] = {
        ...steps[currentStepIndex],
        subSteps: undefined,
      };
      stepList(newActiveSteps);
    }
  }, [paymentMethodData]);

  useEffect(() => {
    if (
      financingStatus &&
      financingStatus.requestPreApprovedCredit &&
      financingStatus.requestPreApprovedCredit.financing &&
      financingStatus.requestPreApprovedCredit.financing.financingStatus ===
        FinancingStatus.Draft
    ) {
      setLoadingInjection(true);
      loadingCredit(CreditStates.Loading);
    }
  }, [financingStatus, financingStatusLoading]);

  useEffect(() => {
    const isPaymentMethodCash =
      paymentMethodData &&
      paymentMethodData.paymentMethod &&
      paymentMethodData.paymentMethod.type === AllowedPaymentMethodType.Cash;
    const isPaymentMethodFinancing =
      paymentMethodData &&
      paymentMethodData.paymentMethod &&
      paymentMethodData.paymentMethod.type === AllowedPaymentMethodType.WithFinancing;
    const isCreditRequested =
      filledPersonalInformation &&
      customerInfo &&
      customerInfo.customer &&
      customerInfo.customer.financingInfo &&
      loadingInjection;

    if (
      (customerInfo && isPaymentMethodCash) ||
      (isPaymentMethodFinancing && isCreditRequested)
    ) {
      next();
    }
  }, [customerInfo, paymentMethodData, loadingInjection]);

  useEffect(() => {
    if (currentSubStepIndex === -1) {
      setFilledPersonalInformation(false);
    } else {
      setFilledPersonalInformation(true);
    }
  }, [currentSubStepIndex]);

  async function isFormValid() {
    try {
      await form.validateFields();
      return true;
    } catch {
      return false;
    }
  }

  return (
    <Spin spinning={customerLoading}>
      <Form
        form={form}
        name="personalInformation"
        scrollToFirstError
        initialValues={{
          country: 'Chile',
          birthday: '',
        }}
      >
        <div style={{ display: 'flex', gap: '15px', flexDirection: 'column' }}>
          <PersonalInformationForm form={form} hide={false} />
          <FinancialInformationForm form={form} />
          <NextButton
            onClick={async () => {
              const isValid = await isFormValid();
              if (isValid) {
                submitCustomerInfo(form.getFieldsValue() as CustomerForm);
              }
            }}
          />
        </div>
      </Form>
    </Spin>
  );
};

export default PersonalInformation;
